import { type MouseEvent, type ReactNode, useId, useMemo, useState } from 'react';

export type ResourceMenuAction = {
  id?: string;
  label: ReactNode;
  icon?: ReactNode;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  divider?: boolean;
  'data-testid'?: string;
};

export type ResourceMenuDivider = {
  type: 'divider';
  id?: string;
};

export type ResourceMenuItem =
  | (ResourceMenuAction & { type?: 'action' })
  | ResourceMenuDivider;

export type ResourceMenuActionsProps = {
  items?: (ResourceMenuItem | null | undefined | false)[];
  actions?: (ResourceMenuItem | null | undefined | false)[];
  tooltip?: string;
  ariaLabel?: string;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  'data-testid'?: string;
};

const ITEM_HEIGHT = 48;

export function useResourceMenuActions(props: ResourceMenuActionsProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const menuId = useId();
  const menuButtonId = useId();

  const items = useMemo(() => {
    const rawList = props.items ?? props.actions ?? [];
    return rawList.filter((item): item is ResourceMenuItem => Boolean(item));
  }, [props.items, props.actions]);

  const hasItems = items.length > 0;

  function handleMenuOpen(event: MouseEvent<HTMLElement>) {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }

  function handleMenuClose(event?: unknown) {
    if (
      event &&
      typeof event === 'object' &&
      'stopPropagation' in event &&
      typeof (event as { stopPropagation: () => void }).stopPropagation === 'function'
    ) {
      (event as { stopPropagation: () => void }).stopPropagation();
    }
    setAnchorEl(null);
  }

  function handleActionClick(
    actionOnClick: (event: MouseEvent<HTMLElement>) => void,
    event: MouseEvent<HTMLElement>,
  ) {
    event.stopPropagation();
    handleMenuClose();
    actionOnClick(event);
  }

  const tooltip = props.tooltip ?? 'Options';
  const ariaLabel = props.ariaLabel ?? tooltip;
  const size = props.size ?? 'small';

  return {
    items,
    hasItems,
    anchorEl,
    isMenuOpen,
    menuId,
    menuButtonId,
    itemHeight: ITEM_HEIGHT,
    tooltip,
    ariaLabel,
    size,
    handleMenuOpen,
    handleMenuClose,
    handleActionClick,
  };
}
